// @flow
import React, {useState, useRef, useEffect, useCallback} from 'react';
import {Icon, Typography} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import {makeStyles} from '@material-ui/styles';
import {useIsFeatureInTier} from '../../../../utils/component';

import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../assets/styles/colors';
import {usePage} from '../../../page/utils';
import HeaderTitle from '../components/HeaderTitle/HeaderTitle';
import SingleQuestion from '../components/SingleQuestion/SingleQuestion';
import LoadingSpinner from '../../../../elements/LoadingSpinner/LoadingSpinner';
import SnackBar from '../../../../elements/SnackBar/SnackBar';

import type {FaqQuestion} from '../duck';
import {useTypedSelector} from '../../../../store';
type Props = {
  isSaving?: boolean,
  isFetchingFaq?: boolean,
  saveFaqSuccess?: boolean,
  saveFaqError?: string,
  questions: FaqQuestion[],
  saveStoreFaqConfig: () => void,
  fetchStoreFaqConfig: () => void,
  editStoreFaqConfig: () => void,
  deleteStoreFaqConfig: ({legalConfigRequest: {[key: string]: any}}) => void,
};

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  section: {
    margin: theme.spacing(1, 0),
  },
  addButton: {
    padding: 0,
    width: '100%',
    borderRadius: '12px',
  },
  boxDashed: {
    width: '100%',
    borderRadius: '12px',
    padding: theme.spacing(2, 0),
    border: `2px dashed ${colors.grey500}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  addText: {
    color: colors.grey900,
    fontWeight: 500,
    fontSize: 14,
  },
  iconButtonAdd: {
    // backgroundColor: colors.grey300,
    borderRadius: '100%',
    marginRight: theme.spacing(1),
  },
  addButtonDisabled: {
    opacity: 0.3,
  },
}));
function Faq(props: Props) {
  const {
    questions,
    isSaving,
    isFetchingFaq,
    saveFaqSuccess,
    saveFaqError,
    saveStoreFaqConfig,
    editStoreFaqConfig,
    deleteStoreFaqConfig,
    fetchStoreFaqConfig,
  } = props;

  const classes = useStyles();
  const containerRef = useRef();
  const legalSetting = useTypedSelector(
    state => state.legalConfig.legalSetting
  );

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [newQuestion, setNewQuestion] = useState(false);
  const [faqQuestions, setFaqQuestions] = useState([]);

  const hasFaqInformationFeature = useIsFeatureInTier('STORE_POLICIES');

  const handleAddMoreQuestion = () => {
    setNewQuestion(true);
    setFaqQuestions(pq => [...pq, {question: '', answer: ''}]);
  };

  const handleCurrentQuestion = useCallback((index: number) => {
    setCurrentQuestion(index);
  }, []);

  const handleUpdateQuestion = useCallback((qs: FaqQuestion, idx) => {
    setFaqQuestions(prev => {
      const aux = [...prev];
      aux[idx] = {...aux[idx], ...qs};
      return aux;
    });
  }, []);

  const handleDeleteFaq = (id: string | number, qs?: FaqQuestion) => {
    if (qs) {
      deleteStoreFaqConfig({legalConfigRequest: {id}});
      setFaqQuestions(prev => prev.filter(q => q.id !== id));
    } else {
      setFaqQuestions(prev => prev.filter((_, idx) => idx !== id));
    }
  };

  useEffect(() => {
    if (newQuestion) {
      if (window && containerRef && containerRef.current) {
        window.scrollTo({
          top: containerRef.current.scrollHeight + 9999,
          behavior: 'smooth',
        });
      }
      setNewQuestion(false);
    }
  }, [newQuestion]);

  useEffect(() => {
    if (questions) {
      setFaqQuestions(prev => {
        if (prev.length === 0) return questions;

        const getContentKey = q =>
          `${(q.question || '').trim()}::${(q.answer || '').trim()}`;

        const serverMap = new Map();
        questions.forEach(q => {
          const key = getContentKey(q);
          if (!serverMap.has(key)) {
            serverMap.set(key, []);
          }
          serverMap.get(key).push(q);
        });

        const usedServerIds = new Set();

        const merged = prev.map(localQ => {
          if (localQ.id) {
            const serverVersion = questions.find(sq => sq.id === localQ.id);
            return serverVersion || localQ;
          }

          const key = getContentKey(localQ);
          const matches = serverMap.get(key) || [];
          const availableMatch = matches.find(m => !usedServerIds.has(m.id));

          if (availableMatch) {
            usedServerIds.add(availableMatch.id);
            return availableMatch;
          }

          return localQ;
        });

        const newFromServer = questions.filter(
          sq => !usedServerIds.has(sq.id) && !prev.some(p => p.id === sq.id)
        );

        return [...merged, ...newFromServer];
      });
    }
  }, [questions]);

  useEffect(() => {
    if (hasFaqInformationFeature) {
      fetchStoreFaqConfig();
    }
  }, [fetchStoreFaqConfig, hasFaqInformationFeature]);

  return (
    <div className={classes.root}>
      {(isSaving || isFetchingFaq) && <LoadingSpinner />}
      <SnackBar
        mode="success"
        message={saveFaqSuccess ? 'Preguntas actualizadas' : ''}
      />
      <SnackBar mode="error" message={!!saveFaqError ? saveFaqError : ''} />
      <HeaderTitle
        title="Preguntas Frecuentes"
        description="Coloca respuestas a las preguntas frecuentes de tus clientes"
        enabledKeyName="faqSettingEnabled"
        enabled={legalSetting?.faqSettingEnabled}
        disabled={!hasFaqInformationFeature}
      />
      <section className={classes.section}>
        {!isFetchingFaq && faqQuestions && faqQuestions.length
          ? faqQuestions.map((qs, idx) => (
              <SingleQuestion
                key={qs.id || idx}
                idx={idx}
                question={qs}
                handleUpdateQuestion={handleUpdateQuestion}
                currentQuestion={currentQuestion}
                handleCurrentQuestion={handleCurrentQuestion}
                saveStoreFaqConfig={saveStoreFaqConfig}
                editStoreFaqConfig={editStoreFaqConfig}
                handleDeleteFaq={handleDeleteFaq}
              />
            ))
          : null}
        <CustomButton
          disabled={!hasFaqInformationFeature}
          className={`${classes.addButton} ${
            !hasFaqInformationFeature ? classes.addButtonDisabled : ''
          }`}
          onClick={handleAddMoreQuestion}
        >
          <div className={classes.boxDashed} ref={containerRef}>
            <Icon size="small" className={classes.iconButtonAdd}>
              <AddIcon style={{color: '#3A3A49'}} />
            </Icon>
            <Typography className={classes.addText}>
              Agregar una pregunta adicional
            </Typography>
          </div>
        </CustomButton>
        <div style={{height: newQuestion ? '120px' : '0px'}} />
      </section>
    </div>
  );
}

export default usePage<Props>(Faq, 'FAQ configuration');
